import React, { useEffect, useState } from 'react';
import useAuth from '../hooks/useAuth';
import useTenant from '../hooks/useTenant';
import { Link } from 'react-router-dom';
import api from '../services/api';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { tenantName } = useTenant();

  // Stats states
  const [classesCount, setClassesCount] = useState<number>(0);
  const [teachersCount, setTeachersCount] = useState<number>(0);
  const [studentsCount, setStudentsCount] = useState<number>(0);
  const [pendingValidations, setPendingValidations] = useState<number>(3);
  const [lockedResultsCount, setLockedResultsCount] = useState<number>(12);
  const [isLoading, setIsLoading] = useState(true);

  // Quick Action Modal states
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const roles = user?.roles || [];
  const hasRole = (r: string) => roles.includes(r);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        // Load classes from the active tenant
        const classesRes = await api.get('/classes');
        if (classesRes.data.success) {
          setClassesCount(classesRes.data.data.length);
        }
        
        // Load teachers from the active tenant
        const teachersRes = await api.get('/teachers');
        if (teachersRes.data.success) {
          setTeachersCount(teachersRes.data.data.length);
        }

        // Mock students count
        setStudentsCount(classesRes.data.data.length * 32 || 96);

      } catch (err) {
        console.error('Error fetching dashboard statistics:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  // -------------------------------------------------------------
  // Render Dashboard for: School Admin
  // -------------------------------------------------------------
  const renderSchoolAdminDashboard = () => {
    return (
      <div className="space-y-6">
        
        {/* Banner with Academic Year Overview */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-page-header mb-1">Administrative Control Panel</h1>
            <p className="text-helper">Active Academic Year: <strong className="text-indigo-600">2026 Term 1</strong> | Status: <strong className="text-emerald-600">Active Session</strong></p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-xl text-[10px] font-bold uppercase tracking-wider border border-indigo-150">
              SaaS Isolation Mode
            </span>
          </div>
        </div>

        {/* Dashboard Core Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-4.5 shadow-sm">
            <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">Students</span>
            <p className="text-2xl font-extrabold text-slate-900 mt-1">{isLoading ? '...' : studentsCount}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4.5 shadow-sm">
            <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">Teachers</span>
            <p className="text-2xl font-extrabold text-slate-900 mt-1">{isLoading ? '...' : teachersCount}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4.5 shadow-sm">
            <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">Classes</span>
            <p className="text-2xl font-extrabold text-slate-900 mt-1">{isLoading ? '...' : classesCount}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4.5 shadow-sm">
            <span className="text-amber-600 font-bold uppercase tracking-wider text-[9px]">Pending Validation</span>
            <p className="text-2xl font-extrabold text-amber-600 mt-1">{pendingValidations}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4.5 shadow-sm">
            <span className="text-indigo-600 font-bold uppercase tracking-wider text-[9px]">Locked Subjects</span>
            <p className="text-2xl font-extrabold text-indigo-600 mt-1">{lockedResultsCount}</p>
          </div>
        </div>

        {/* Quick Action Grid */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
          <h2 className="text-section-header">Administrative Quick Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <button onClick={() => setActiveModal('student')} className="p-3 text-center bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-200 transition-all space-y-1">
              <span className="text-xl block">🎓</span>
              <span className="font-semibold text-[9px] uppercase tracking-wider text-slate-700 block">Add Student</span>
            </button>
            <button onClick={() => setActiveModal('teacher')} className="p-3 text-center bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-200 transition-all space-y-1">
              <span className="text-xl block">🧑‍🏫</span>
              <span className="font-semibold text-[9px] uppercase tracking-wider text-slate-700 block">Add Teacher</span>
            </button>
            <Link to="/manage-classes" className="p-3 text-center bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-200 transition-all space-y-1 block">
              <span className="text-xl block">🔗</span>
              <span className="font-semibold text-[9px] uppercase tracking-wider text-slate-700 block">Assign Teacher</span>
            </Link>
            <Link to="/manage-classes" className="p-3 text-center bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-200 transition-all space-y-1 block">
              <span className="text-xl block">🏫</span>
              <span className="font-semibold text-[9px] uppercase tracking-wider text-slate-700 block">Create Class</span>
            </Link>
            <Link to="/classes" className="p-3 text-center bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-200 transition-all space-y-1 block">
              <span className="text-xl block">🔒</span>
              <span className="font-semibold text-[9px] uppercase tracking-wider text-slate-700 block">Lock Results</span>
            </Link>
            <Link to="/reports" className="p-3 text-center bg-slate-50 hover:bg-slate-100/80 rounded-xl border border-slate-200 transition-all space-y-1 block">
              <span className="text-xl block">📊</span>
              <span className="font-semibold text-[9px] uppercase tracking-wider text-slate-700 block">Reports Hub</span>
            </Link>
          </div>
        </div>

        {/* Analytics and Comparison Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Class Performance comparison */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm lg:col-span-2 space-y-4">
            <h2 className="text-section-header">Class Performance & Trend Breakdown</h2>
            
            <div className="h-44 w-full flex items-end justify-between px-2 pt-4 border-b border-slate-200">
              <div className="flex flex-col items-center w-12 gap-2">
                <div className="w-8 bg-indigo-600 rounded-t-md hover:opacity-90 transition-all cursor-pointer" style={{ height: '110px' }} title="Grade 8A: 78%"></div>
                <span className="text-[9px] font-bold text-slate-500">G-8A</span>
              </div>
              <div className="flex flex-col items-center w-12 gap-2">
                <div className="w-8 bg-indigo-650 rounded-t-md hover:opacity-90 transition-all cursor-pointer" style={{ height: '95px' }} title="Grade 8B: 68%"></div>
                <span className="text-[9px] font-bold text-slate-500">G-8B</span>
              </div>
              <div className="flex flex-col items-center w-12 gap-2">
                <div className="w-8 bg-emerald-600 rounded-t-md hover:opacity-90 transition-all cursor-pointer" style={{ height: '135px' }} title="Grade 9A: 92%"></div>
                <span className="text-[9px] font-bold text-slate-500">G-9A</span>
              </div>
              <div className="flex flex-col items-center w-12 gap-2">
                <div className="w-8 bg-indigo-600 rounded-t-md hover:opacity-90 transition-all cursor-pointer" style={{ height: '80px' }} title="Grade 9B: 58%"></div>
                <span className="text-[9px] font-bold text-slate-500">G-9B</span>
              </div>
              <div className="flex flex-col items-center w-12 gap-2">
                <div className="w-8 bg-indigo-650 rounded-t-md hover:opacity-90 transition-all cursor-pointer" style={{ height: '105px' }} title="Grade 10A: 74%"></div>
                <span className="text-[9px] font-bold text-slate-500">G-10A</span>
              </div>
            </div>
            <p className="text-[9px] text-slate-400 text-center font-bold uppercase tracking-wider">Historical Term averages by class stream</p>
          </div>

          {/* Subject Analysis list */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
            <h2 className="text-section-header">Top Performing Subjects</h2>
            <div className="space-y-3.5 text-nav">
              <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                <span className="font-semibold text-slate-700">Mathematics</span>
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded text-[9px] font-bold">84% AVG</span>
              </div>
              <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                <span className="font-semibold text-slate-700">English Language</span>
                <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded text-[9px] font-bold">78% AVG</span>
              </div>
              <div className="flex justify-between items-center border-b border-slate-50 pb-2">
                <span className="font-semibold text-slate-700">Biology / Science</span>
                <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded text-[9px] font-bold">72% AVG</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  };

  // -------------------------------------------------------------
  // Render Dashboard for: Teachers
  // -------------------------------------------------------------
  const renderTeacherDashboard = () => {
    return (
      <div className="space-y-6">
        
        {/* Welcome block card */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-page-header mb-1">Welcome, {user?.first_name} {user?.last_name}</h1>
            <p className="text-helper">Continuous Assessment (CA) Portal | Institutional Term Records.</p>
          </div>
          <Link to="/classes" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl shadow-sm transition-colors text-nav text-center whitespace-nowrap">
            Launch Score Sheets
          </Link>
        </div>

        {/* Dynamic Teacher Action Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px] block">My Assigned Subjects</span>
            <div className="space-y-1 text-nav">
              <p className="font-bold text-slate-800">📐 Mathematics (Grade 9A)</p>
              <p className="font-bold text-slate-800">🧪 Science (Grade 10B)</p>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-amber-600 font-bold uppercase tracking-wider text-[9px] block">Pending CA Submissions</span>
            <div className="text-nav font-bold text-slate-800 space-y-1">
              <p className="text-amber-700">⚠️ Term 1 Mid-Term: Grade 9A</p>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px] block">Form Teacher Duties</span>
            <p className="font-bold text-indigo-600 text-nav">🏫 Class Tutor: Grade 9A</p>
          </div>
        </div>

        {/* Large visual cards for fast mobile actions */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="text-section-header">Fast Assessment Launchers</h2>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link to="/classes" className="p-5 bg-indigo-50/50 hover:bg-indigo-50 border border-indigo-150 rounded-2xl block space-y-2 group transition-all">
              <span className="text-2xl">📝</span>
              <h3 className="font-extrabold text-indigo-950 text-card-title leading-tight">Enter Marks Sheet</h3>
              <p className="text-[9px] text-indigo-600 font-semibold">Verify scores and sync drafts instantly with autosave indicator.</p>
            </Link>
            <Link to="/reports" className="p-5 bg-slate-50 hover:bg-slate-100/60 border border-slate-200 rounded-2xl block space-y-2 group transition-all">
              <span className="text-2xl">📊</span>
              <h3 className="font-extrabold text-slate-900 text-card-title leading-tight">Report Analytics</h3>
              <p className="text-[9px] text-slate-500 font-medium">Render cohort performance ratios, comparisons, and print summaries.</p>
            </Link>
          </div>
        </div>

      </div>
    );
  };

  // -------------------------------------------------------------
  // Render Dashboard for: Headteacher / Deputy
  // -------------------------------------------------------------
  const renderHeadteacherDashboard = () => {
    return (
      <div className="space-y-6">
        
        {/* Header Summary */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
          <h1 className="text-page-header mb-1">Academic Administration Console</h1>
          <p className="text-helper">Verify term results submissions, audit class grades sheets, and authorize bulk report releases.</p>
        </div>

        {/* Telemetry charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* SVG Pass rate comparison */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm lg:col-span-2 space-y-4">
            <h2 className="text-section-header">School Term Pass-Rate Trends</h2>
            <div className="relative pt-6 px-2">
              <svg className="w-full h-36 overflow-visible" viewBox="0 0 500 100" preserveAspectRatio="none">
                <line x1="0" y1="20" x2="500" y2="20" stroke="#f1f5f9" strokeWidth="1" />
                <line x1="0" y1="50" x2="500" y2="50" stroke="#f1f5f9" strokeWidth="1" />
                <line x1="0" y1="80" x2="500" y2="80" stroke="#f1f5f9" strokeWidth="1" />
                
                <path d="M 0 85 L 100 65 L 200 45 L 300 30 L 400 25 L 500 15" fill="none" stroke="var(--color-primary)" strokeWidth="3" />
                
                <circle cx="0" cy="85" r="4" fill="var(--color-primary)" />
                <circle cx="100" cy="65" r="4" fill="var(--color-primary)" />
                <circle cx="200" cy="45" r="4" fill="var(--color-primary)" />
                <circle cx="300" cy="30" r="4" fill="var(--color-primary)" />
                <circle cx="400" cy="25" r="4" fill="var(--color-primary)" />
                <circle cx="500" cy="15" r="4" fill="var(--color-primary)" />
              </svg>
              <div className="flex justify-between text-[8px] font-bold text-slate-400 mt-2">
                <span>Term 1 2025</span>
                <span>Term 2 2025</span>
                <span>Term 3 2025</span>
                <span>Term 1 2026</span>
              </div>
            </div>
          </div>

          {/* Validation Workflows Queue */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
            <h2 className="text-section-header">Workflow Audit Alerts</h2>
            <div className="space-y-3">
              <div className="p-3 bg-amber-50/60 border border-amber-100 rounded-xl flex items-center justify-between">
                <div>
                  <p className="font-semibold text-card-title text-amber-900 leading-tight">Grade 9A Mathematics</p>
                  <p className="text-[8px] text-slate-500">Awaiting validation checks</p>
                </div>
                <Link to="/reports" className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-[9px] font-bold shadow-sm">
                  Review
                </Link>
              </div>
              <div className="p-3 bg-amber-50/60 border border-amber-100 rounded-xl flex items-center justify-between">
                <div>
                  <p className="font-semibold text-card-title text-amber-900 leading-tight">Grade 10B Biology</p>
                  <p className="text-[8px] text-slate-500">Submitted by Sarah Banda</p>
                </div>
                <Link to="/reports" className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-[9px] font-bold shadow-sm">
                  Review
                </Link>
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  };

  const renderActiveDashboard = () => {
    if (hasRole('school_admin')) {
      return renderSchoolAdminDashboard();
    } else if (hasRole('headteacher') || hasRole('deputy_headteacher')) {
      return renderHeadteacherDashboard();
    } else {
      return renderTeacherDashboard();
    }
  };

  return (
    <div className="max-w-6xl mx-auto pb-8 relative">
      {renderActiveDashboard()}

      {/* Dynamic Popups for Quick Add Tasks */}
      {activeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-slate-100">
            <h2 className="text-section-header mb-4">
              {activeModal === 'student' ? 'Quick Add Student Account' : 'Quick Add Teacher Account'}
            </h2>
            <form onSubmit={(e) => { e.preventDefault(); setActiveModal(null); }} className="space-y-4">
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Full Name</label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body outline-none" required />
              </div>
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Email Reference</label>
                <input type="email" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body outline-none" required />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setActiveModal(null)} className="px-4 py-2 text-nav font-bold text-slate-500 hover:text-slate-700">Cancel</button>
                <button type="submit" className="px-5 py-2 text-nav font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-sm">Save Account</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
